<template>
  <div id="orders-profit-by-position-type">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title">
          {{ $t(`views.dashboardDetail.orders.ordersProfitByPositionType.title`) }}
        </h1>
        <v-spacer />
        <ShowCashButton
          v-show="!filters.positionType && !alwaysShowCash"
          class="mt-1"
          :showCash="showCash"
          @showCashChanged="showCashChanged"
        />
        <ExportButton
          class="mt-1"
          :click="downloadCsv"
          :loading="exportLoading"
          :disabled="!summary.length"
        />
      </v-card-title>
      <v-card-text class="mt-4 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart
          v-if="showChart && loadedData"
          :chartData="barChartData"
          :chartOptions="barChartOptions"
        />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="orders-profit-by-position-type-table"
            :headers="headers"
            :items="summary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="itemsPerPage"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
          >
            <template v-slot:item._id="{ item }">
              <div class="items-flex">
                <span>
                  <b>{{ $t(`common.positionTypes.${item._id}`) }}</b>
                </span>
                <template v-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon
                      :text="$t(`views.dashboardDetail.orders.ordersByPosition.warning`)"
                    />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.totalReceivedWithComissionBaseCurrency="{ item }">
              <currency
                v-if="item.totalReceivedWithComissionBaseCurrency"
                :value="item.totalReceivedWithComissionBaseCurrency"
                :currency="user.baseCurrency"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.totalCostWithComissionBaseCurrency="{ item }">
              <currency
                v-if="item.totalCostWithComissionBaseCurrency"
                :value="item.totalCostWithComissionBaseCurrency"
                :currency="user.baseCurrency"
                :hideDataNoPlanUser="hideDataNoPlanUser"
              />
              <span v-else></span>
            </template>
            <template v-slot:item.profitWithComissionBaseCurrency="{ item }">
              <currency
                :value="item.profitWithComissionBaseCurrency || 0"
                :currency="user.baseCurrency"
                :text-green="item.profitWithComissionBaseCurrency > 0"
                :text-red="item.profitWithComissionBaseCurrency < 0"
                :negative="item.profitWithComissionBaseCurrency < 0"
                strong
                :hideDataNoPlanUser="hideDataNoPlanUser"
              />
            </template>
            <template v-slot:item.destinationRetentionBaseCurrency="{ item }">
              <currency
                :value="item.destinationRetentionBaseCurrency || 0"
                :currency="user.baseCurrency"
              />
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import FileSaver from 'file-saver'
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getOrderProfitSummary, exportOrderProfitSummaryCsv } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'
import TableTicker from '@/views/components/common/TableTicker'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import TableCoreRecreating from '@/views/components/common/positions/TableCoreRecreating'
import ExportButton from '@/views/components/common/ExportButton'
import ShowCashButton from '@/views/components/common/ShowCashButton'

export default {
  components: {
    Currency,
    BarChart,
    BarLoading,
    TableTicker,
    WarningIcon,
    TableCoreRecreating,
    ExportButton,
    ShowCashButton,
  },
  props: {
    showTable: {
      type: Boolean,
      default: true,
    },
    showChart: {
      type: Boolean,
      default: true,
    },
    alwaysShowCash: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 5,
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    summary: [],
    exportLoading: false,
    totalRow: null,
    currentPage: null,
    loadedData: false,
    barChartData: {
      labels: null,
      datasets: null,
    },
    filters: [],
    showCash: false,
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this

      return {
        maintainAspectRatio: false,
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    headers() {
      const headers = [
        {
          text: this.$t('common.type'),
          value: '_id',
          divider: true,
        },
        {
          text: `${this.$t(
            'views.common.ordersProfit.totalReceivedWithComission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalReceivedWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.common.ordersProfit.totalCostWithComission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalCostWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.common.ordersProfit.profitWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'profitWithComissionBaseCurrency',
          align: 'right',
        },
        {
          text: `${this.$t('views.common.ordersProfit.destinationRetention')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'destinationRetentionBaseCurrency',
          align: 'right',
        },
      ]
      return headers
    },
  },
  watch: {
    alwaysShowCash: {
      handler() {
        if (this.alwaysShowCash) this.showCash = true
      },
      immediate: true,
    },
  },
  methods: {
    someNullExchangeRate(item) {
      return (
        (item.exchangeRates && item.exchangeRates.some((elem) => elem === null)) ||
        (item.totalCostsWithComissionBaseCurrency &&
          item.totalCostsWithComissionBaseCurrency.some((elem) => elem === null)) ||
        (item.totalReceivedsWithComissionBaseCurrency &&
          item.totalReceivedsWithComissionBaseCurrency.some((elem) => elem === null))
      )
    },
    itemRowBackground(item) {
      if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    createBarChartObject(data) {
      this.barChartData.datasets = [
        {
          label: `${this.$t('views.common.ordersProfit.profitWithComission')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          data: map(data, 'profitWithComissionBaseCurrency'),
          backgroundColor: data.map((item) =>
            item.profitWithComissionBaseCurrency < 0
              ? this.$vuetify.theme.dark
                ? DARK_DEFAULT_PALETTE.chartsBarRed
                : DEFAULT_PALETTE.chartsBarRed
              : this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarGreen
              : DEFAULT_PALETTE.chartsBarGreen
          ),
        },
      ]
      this.barChartData.labels = map(data, (item) => {
        return this.$t(`common.positionTypes.${item._id}`)
      })
    },
    async loadSummary(filters) {
      this.loadedData = false
      this.filters = filters
      try {
        let summary = await getOrderProfitSummary({
          group: 'byPositionType',
          filter: {
            positionType: !this.showCash ? { $ne: 'cash' } : undefined,
            ...filters,
          },
        })
        summary = summary.data

        this.summary = summary
        this.currentPage = 1

        this.createBarChartObject(summary)
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
    async downloadCsv() {
      this.exportLoading = true

      const nowFormatted = DateTime.now().toFormat('yyyy_MM_dd HH_mm_ss')
      try {
        const result = await exportOrderProfitSummaryCsv({
          group: 'byPositionType',
          filter: {
            positionType: !this.showCash ? { $ne: 'cash' } : undefined,
            ...this.filters,
          },
        })
        const blob = new Blob([result.data], { type: 'text/csv;charset=utf-8' })
        FileSaver.saveAs(
          blob,
          `Filios - ${this.$t(
            `views.dashboardDetail.orders.ordersProfitByPositionType.title`
          )} ${nowFormatted}.csv`
        )
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }

      this.exportLoading = false
    },
    showCashChanged(showCash) {
      this.showCash = showCash
      this.loadSummary(this.filters)
    },
  },
}
</script>

<style lang="scss">
#orders-profit-by-position-type {
  #orders-profit-by-position-type-table {
    .items-flex {
      display: flex;
    }
  }
}
</style>
